/**
 * 一键清除用户学习数据的确认弹窗（旧 UserVocabularyView.vue 的确认框）：
 * 输入用户 ID 一致才执行；输错时提示并保持弹窗打开
 */
import { useState } from 'react'
import { App, Input, Modal, Typography } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { useClearUserData } from './hooks'
import styles from './UserVocabularyPage.module.css'

interface ClearUserDataModalProps {
  open: boolean
  userId: number
  onClose: () => void
  onCleared: () => void
}

export function ClearUserDataModal({ open, userId, onClose, onCleared }: Readonly<ClearUserDataModalProps>) {
  const { message } = App.useApp()
  const [inputUserId, setInputUserId] = useState('')
  const clearData = useClearUserData()

  const close = () => {
    setInputUserId('')
    onClose()
  }

  const handleOk = async () => {
    if (inputUserId !== String(userId)) {
      message.error('用户ID输入错误，操作已取消')
      return
    }
    try {
      const result = await clearData.mutateAsync(userId)
      if (result.success && result.data) {
        const stats = result.data.deleted_stats
        message.success({
          content: `已清除 ${stats.vocabulary_count} 个词汇分值、${stats.evidence_count} 条证据和 ${stats.theme_instances_count} 个主题实例`,
          duration: 3
        })
        close()
        onCleared()
      } else {
        message.error(result.message || '清除数据失败')
      }
    } catch (error: any) {
      console.error('清除数据失败:', error)
      message.error('清除数据失败：' + (error.response?.data?.detail || error.message))
    }
  }

  return (
    <Modal
      open={open}
      width={520}
      title={
        <span>
          <ExclamationCircleOutlined className={styles.warningIcon} /> ⚠️ 危险操作：清空用户学习数据
        </span>
      }
      okText="确认清除"
      okButtonProps={{ danger: true, loading: clearData.isPending }}
      cancelText="取消"
      onOk={handleOk}
      onCancel={close}
    >
      <Typography.Text type="danger" strong>
        此操作将永久删除以下数据，不可恢复！
      </Typography.Text>
      <ul className={styles.clearList}>
        <li>词汇熟悉度分值</li>
        <li>学习证据事件</li>
        <li>用户主题实例</li>
      </ul>
      <p className={styles.confirmHint}>请输入用户ID「{userId}」以确认删除：</p>
      <Input
        aria-label="确认用户ID"
        placeholder={`请输入 ${userId}`}
        value={inputUserId}
        onChange={(event) => setInputUserId(event.target.value)}
      />
    </Modal>
  )
}
